import { Link, router, useForm, usePage } from '@inertiajs/react'
import { useState } from 'react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { Switch } from '@/shared/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { PhoneCodeSelect } from '@/shared/ui/phone-code-select'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  accessAdminUserPath,
  adminUserPath,
  adminUsersPath,
  manageAdminUserPath,
  resendAccessAdminUserPath,
} from '@/shared/lib/routes'

interface UserFormData {
  id: number | null
  firstName: string
  lastName: string
  middleName: string | null
  email: string
  phone: string | null
  phoneCode: string | null
  jobTitle: string | null
  employeeId: string | null
  federatedUserId: string | null
  role: string | null
  canViewConfidentialInfo: boolean
  viewOnly: boolean
  disabled: boolean
  pendingPasswordSetup: boolean
}

interface Props {
  user: UserFormData | null
  roles: string[]
  isEdit: boolean
  currentUserIsAdmin: boolean
  flash?: { alert?: string; notice?: string }
  [key: string]: unknown
}

interface FormErrors {
  [key: string]: string[] | undefined
}

const ROLE_LABELS: Record<string, string> = {
  customer_admin: t('admin.users.form.roleCustomer_admin'),
  customer_supervisor: t('admin.users.form.roleCustomer_supervisor'),
  customer_user: t('admin.users.form.roleCustomer_user'),
}

const CUSTOMER_ROLES: readonly string[] = ['customer_admin', 'customer_supervisor', 'customer_user']

const fieldErrorMessages = (errors: FormErrors, field: string): string[] | undefined => {
  const camelField = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
  return errors[field] ?? errors[camelField]
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = fieldErrorMessages(errors, field)
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const UserForm = () => {
  const { user, roles, isEdit, currentUserIsAdmin, flash } = usePage<Props>().props
  const showResendPassword = isEdit && currentUserIsAdmin && user?.id

  useBreadcrumbs(
    isEdit && user
      ? [
          { label: t('nav.administration') },
          { label: t('nav.users'), href: adminUsersPath() },
          {
            // REBUILD-525: show the user record id next to the name in the edit breadcrumb.
            label: `${user.lastName}, ${user.firstName} (ID:${user.id})`,
            href: adminUserPath(user.id!),
          },
          { label: t('admin.users.form.editTitle') },
        ]
      : [
          { label: t('nav.administration') },
          { label: t('nav.users'), href: adminUsersPath() },
          { label: t('admin.users.form.newTitle') },
        ],
  )

  const form = useForm({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    middleName: user?.middleName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    phoneCode: user?.phoneCode ?? '',
    jobTitle: user?.jobTitle ?? '',
    employeeId: user?.employeeId ?? '',
    federatedUserId: user?.federatedUserId ?? '',
    role: user?.role ?? '',
    canViewConfidentialInfo: user?.canViewConfidentialInfo ?? false,
    viewOnly: user?.viewOnly ?? false,
  })

  const errors: FormErrors = (usePage<{ errors?: FormErrors }>().props.errors ?? {}) as FormErrors
  const canSubmit = useCan(Resources.ADMIN_USER, isEdit ? Actions.UPDATE : Actions.CREATE)
  const canManage = useCan(Resources.ADMIN_USER, Actions.UPDATE)
  const [manageModalOpen, setManageModalOpen] = useState(false)

  const submitManage = () => {
    if (!user?.id) return
    router.patch(manageAdminUserPath(user.id), { disabled: !user.disabled })
    setManageModalOpen(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ user: data }))
    if (isEdit && user?.id) {
      form.patch(adminUserPath(user.id))
    } else {
      form.post(adminUsersPath())
    }
  }

  const cancelPath = isEdit && user?.id ? adminUserPath(user.id) : adminUsersPath()

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? t('admin.users.form.editTitle') : t('admin.users.form.newTitle')}
        </h1>

        {flash?.notice && (
          <div className="mb-4 rounded-md border border-emerald-500/50 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {flash.notice}
          </div>
        )}

        {flash?.alert && (
          <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {flash.alert}
          </div>
        )}

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {errors.base.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        {showResendPassword && (
          <div className="mb-6 flex flex-col gap-3 rounded-md border border-muted bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {user.pendingPasswordSetup
                ? t('admin.users.form.pendingPasswordSetupHint')
                : t('admin.users.form.resendPasswordHint')}
            </p>
            <Button asChild variant="outline" className="shrink-0 bg-white">
              <Link href={resendAccessAdminUserPath(user.id!)} method="post" as="button">
                {user.pendingPasswordSetup
                  ? t('admin.users.resendAccessButton')
                  : t('admin.users.resendPasswordButton')}
              </Link>
            </Button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">{t('admin.users.form.firstNameLabel')} *</Label>
              <Input
                id="firstName"
                value={form.data.firstName}
                onChange={(e) => form.setData('firstName', e.target.value)}
              />
              <FieldError errors={errors} field="first_name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">{t('admin.users.form.lastNameLabel')} *</Label>
              <Input
                id="lastName"
                value={form.data.lastName}
                onChange={(e) => form.setData('lastName', e.target.value)}
              />
              <FieldError errors={errors} field="last_name" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="middleName">{t('admin.users.form.middleNameLabel')}</Label>
            <Input
              id="middleName"
              value={form.data.middleName ?? ''}
              onChange={(e) => form.setData('middleName', e.target.value)}
            />
            <FieldError errors={errors} field="middle_name" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">{t('admin.users.form.emailLabel')} *</Label>
            {isEdit ? (
              <Input id="email" value={form.data.email} readOnly className="bg-muted" />
            ) : (
              <Input
                id="email"
                type="email"
                value={form.data.email}
                onChange={(e) => form.setData('email', e.target.value)}
              />
            )}
            <FieldError errors={errors} field="email" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('admin.users.form.phoneCodeLabel')}</Label>
              <PhoneCodeSelect
                value={form.data.phoneCode ?? ''}
                onValueChange={(value) => form.setData('phoneCode', value)}
              />
              <FieldError errors={errors} field="phone_code" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('admin.users.form.phoneLabel')}</Label>
              <Input
                id="phone"
                type="tel"
                value={form.data.phone ?? ''}
                onChange={(e) =>
                  form.setData('phone', e.target.value.replace(/[^\d+\-\s().]/g, ''))
                }
              />
              <FieldError errors={errors} field="phone" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">{t('admin.users.form.jobTitleLabel')}</Label>
            <Input
              id="jobTitle"
              value={form.data.jobTitle ?? ''}
              onChange={(e) => form.setData('jobTitle', e.target.value)}
            />
            <FieldError errors={errors} field="job_title" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="employeeId">{t('admin.users.form.employeeIdLabel')}</Label>
            <Input
              id="employeeId"
              value={form.data.employeeId ?? ''}
              onChange={(e) => form.setData('employeeId', e.target.value)}
            />
            <FieldError errors={errors} field="employee_id" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role">{t('admin.users.form.roleLabel')}</Label>
            <Select
              value={form.data.role ?? ''}
              onValueChange={(value) => form.setData('role', value)}
            >
              <SelectTrigger id="role">
                <SelectValue placeholder={t('admin.users.form.rolePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {ROLE_LABELS[role] ?? role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={errors} field="role" />
          </div>

          {isEdit && (
            <div className="flex items-start gap-3 rounded-md border p-4">
              <Switch
                id="viewOnly"
                checked={form.data.viewOnly}
                onCheckedChange={(checked) => form.setData('viewOnly', checked)}
              />
              <div className="space-y-1">
                <Label htmlFor="viewOnly">{t('admin.users.form.viewOnlyLabel')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('admin.users.form.viewOnlyDescription')}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="canViewConfidentialInfo"
              checked={form.data.canViewConfidentialInfo}
              onCheckedChange={(checked) =>
                form.setData('canViewConfidentialInfo', checked === true)
              }
            />
            <Label htmlFor="canViewConfidentialInfo">
              {t('admin.users.form.canViewConfidentialLabel')}
            </Label>
          </div>

          {isEdit && user?.id && canManage && (
            <div className="flex items-center justify-between gap-3 rounded-md border p-4">
              <div className="space-y-1">
                <Label className={user.disabled ? 'text-destructive' : 'text-muted-foreground'}>
                  {user.disabled
                    ? t('admin.users.form.accountDisabledLabel')
                    : t('admin.users.form.accountActiveLabel')}
                </Label>
              </div>
              <Button
                type="button"
                variant={user.disabled ? 'outline' : 'destructive'}
                onClick={() => setManageModalOpen(true)}
              >
                {user.disabled
                  ? t('admin.users.form.enableButton')
                  : t('admin.users.form.disableButton')}
              </Button>
            </div>
          )}

          <AlertDialog open={manageModalOpen} onOpenChange={setManageModalOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {user?.disabled
                    ? t('admin.users.form.enableModalTitle')
                    : t('admin.users.form.disableModalTitle')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {user?.disabled
                    ? t('admin.users.form.enableModalBody')
                    : t('admin.users.form.disableModalBody')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('admin.users.form.modalCancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={submitManage}>
                  {t('admin.users.form.modalConfirm')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {isEdit ? t('admin.users.form.submitUpdate') : t('admin.users.form.submitCreate')}
              </Button>
            )}
            {isEdit && currentUserIsAdmin && CUSTOMER_ROLES.includes(user?.role ?? '') && (
              <Button asChild variant="outline">
                <Link href={accessAdminUserPath(user!.id!)}>
                  {t('admin.users.manageAccessButton')}
                </Link>
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => router.visit(cancelPath)}>
              {t('admin.users.form.cancelButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserForm
